import type { Callback } from 'amplitude-js';
import {
  AnalyticsEventProperties,
  AnalyticsEventValue,
  FunnelProperties,
  PaymentFormCompletedProperties,
  PaymentFormStateProperties,
} from 'core/analytics/entities';
import { UtmTags } from 'core/user/entities';
import { AmplitudeService } from '../../interfaces';

export class AmplitudeServiceDecorator implements AmplitudeService {
  constructor(protected readonly amplitudeService: AmplitudeService) {}

  logEvent(
    eventName: string,
    data?: Record<string, unknown>,
    callback?: Callback,
    errorCallback?: Callback,
  ) {
    return this.amplitudeService.logEvent(eventName, data, callback, errorCallback);
  }

  setUserProperties(userData: Record<string, unknown>) {
    return this.amplitudeService.setUserProperties(userData);
  }

  setFunnelProperties(properties: FunnelProperties) {
    return this.amplitudeService.setFunnelProperties(properties);
  }

  setUtmTags(tags: UtmTags) {
    return this.amplitudeService.setUtmTags(tags);
  }

  getDeviceId() {
    return this.amplitudeService.getDeviceId();
  }

  getSessionId() {
    return this.amplitudeService.getSessionId();
  }

  trackEvent(event: AnalyticsEventValue, properties?: AnalyticsEventProperties) {
    return this.amplitudeService.trackEvent(event, properties);
  }

  setUserId(userId: string) {
    return this.amplitudeService.setUserId(userId);
  }

  setUserEmail(email: string) {
    return this.amplitudeService.setUserEmail(email);
  }

  setUserIdProperty(id: string) {
    return this.amplitudeService.setUserIdProperty(id);
  }

  techPageOpened(data: { page: string; context: string; userId: string }) {
    return this.amplitudeService.techPageOpened(data);
  }

  trackPaymentFormState(properties: PaymentFormStateProperties) {
    return this.amplitudeService.trackPaymentFormState(properties);
  }

  paymentFormCompleted(properties: PaymentFormCompletedProperties) {
    return this.amplitudeService.paymentFormCompleted(properties);
  }

  paymentRetryScreenOpened() {
    this.trackEvent('funnel_modal_open', { screen_context: 'payment_retry' });
  }

  paymentRetryTryAgainClicked() {
    this.trackEvent('funnel_modal_click', { screen_context: 'payment_retry', value: 'try_again' });
  }

  paymentRetryCloseClicked() {
    this.trackEvent('funnel_modal_click', { screen_context: 'payment_retry', value: 'close' });
  }

  setUserAge(age: number) {
    return this.amplitudeService.setUserAge(age);
  }

  setFeatureFlagsAttributes(attributes: Record<string, unknown>) {
    return this.amplitudeService.setFeatureFlagsAttributes(attributes);
  }
}
